<template>
	<header ref="el" data-component="header">
		<span class="inner-bg">
			<span class="fade" />
		</span>
		<div class="g-row header-row">
			<div class="g-col xxl-3 xs-12 xs-offset-1 side-content">
				<div class="back-wrapper">
					<Button tone="secondary" icon="ic-arrow-left" size="small" :click-func="goBackToInsights" />
					<span class="-body-regular label">Back</span>
				</div>

				<div v-if="tldr" class="tldr desktop">
					<p class="-body-medium title">{{ tldr.title }}</p>
					<p class="-body-regular summary">{{ tldr.summary }}</p>
				</div>
				<div v-if="sideCta" class="call-to-action desktop">
					<p class="-headline-5 title">{{ sideCta.title }}</p>
					<Button v-bind="sideCta.cta" tone="primary" />
				</div>
			</div>
			<div class="g-col xxl-10 xxl-offset-2 xs-14 xs-offset-1 main-content">
				<h2 class="-title-3 category-title">{{ category }}</h2>
				<h1 class="-title-3 title main-title">{{ title }}</h1>
				<div class="-label-regular specs">
					<span>{{ readTime }} min read</span>
					<span>/</span>
					<span>{{ formattedDate }}</span>
				</div>

				<div v-if="tldr" class="tldr mobile">
					<p class="-body-medium title">{{ tldr.title }}</p>
					<p class="-body-regular summary">{{ tldr.summary }}</p>
				</div>

				<p class="-headline-4 subtitle">{{ subtitle }}</p>

				<div v-if="persons" class="persons">
					<p class="-body-small-medium title">A conversation between:</p>
					<div class="persons-wrapper">
						<div v-for="(person, index) in persons" :key="person._key || index" class="person">
							<div class="author-meta">
								<div v-if="person.authorImage" class="author-image-wrapper">
									<ImageAsset :asset="person.authorImage" class="author-image" />
								</div>
								<div class="author-copy">
									<p v-if="person.authorName" class="-body-small-medium author-name">{{ person.authorName }}</p>
									<p v-if="person.authorRole" class="-body-small-regular author-role">{{ person.authorRole }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-if="media" class="image-wrapper" :style="{ '--article-header-media-clip-path': `url(#${imageClipPathId})` }">
					<svg class="svg-mask" viewBox="0 0 796 520" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
						<defs>
							<clipPath :id="imageClipPathId" clipPathUnits="objectBoundingBox">
								<path
									d="M0.984925 0 C0.99325 0 1 0.010332 1 0.023077 V0.976923 C1 0.989667 0.99325 1 0.984925 1 H0.415928 C0.391971 1 0.369109 0.989287 0.352794 0.96879 L0.351251 0.966821 C0.334936 0.946294 0.312074 0.934615 0.288117 0.934615 H0.015075 C0.006749 0.934615 0 0.924283 0 0.911538 V0.023077 C0 0.010332 0.006749 0 0.015075 0 H0.984925 Z"
								/>
							</clipPath>
						</defs>
					</svg>
					<div class="media-mask">
						<MediaAsset :media="media" />
					</div>
				</div>
			</div>
		</div>
	</header>
</template>

<script setup>
import { gsap, SplitText } from 'gsap/all'

import { useEmitter } from '~/composables/core'
import { MediaAsset, ImageAsset } from '~/components/core'
import { Button } from '~/components/primitives'

import.meta.client && gsap.registerPlugin(SplitText)

const props = defineProps({
	title: { type: String, default: undefined },
	category: { type: String, default: undefined },
	date: { type: String, default: undefined },
	readTime: { type: [Number, String], default: undefined },
	subtitle: { type: String, default: undefined },
	tldr: { type: Object, default: undefined },
	sideCta: { type: Object, default: undefined },
	persons: { type: Array, default: undefined },
	media: { type: Object, default: undefined },
})

const ALL_CATEGORY = 'all'
const el = ref(null)
const context = inject('pageContext')
const route = useRoute()
const emitter = useEmitter()
const headerRouteKey = context?.$page?.slug || ''
let entryTimeline = null
let splitTitle = null
let hasAnimated = false
let tldrForEntry = null

const getFirstQueryValue = (value) => (Array.isArray(value) ? value[0] : value)
const normalizeSlug = (value) => {
	if (value === undefined || value === null) return ''
	return String(value)
		.trim()
		.replace(/^\/+|\/+$/g, '')
		.toLowerCase()
}
const normalizeId = (value) => {
	if (value === undefined || value === null) return ''
	return String(value)
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
}

const previousCategory = computed(() => {
	const category = normalizeSlug(getFirstQueryValue(route.query?.category))
	if (!category || category === ALL_CATEGORY) return ''
	return category
})
const imageClipPathId = computed(() => `article-header-image-clip-path-${normalizeId(props.title) || 'default'}`)

const formattedDate = computed(() => {
	const value = props.date
	if (!value) return ''

	const dateValue = new Date(value)
	if (Number.isNaN(dateValue.getTime())) return value

	return dateValue.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
})

const backToInsightsRoute = computed(() => {
	if (!previousCategory.value) return { path: '/insights' }

	return {
		path: '/insights',
		query: {
			category: previousCategory.value,
		},
	}
})

const goBackToInsights = async () => {
	await navigateTo(backToInsightsRoute.value)
}

const getVisibleTldrElement = () => {
	if (!import.meta.client || !el.value) return null

	const tldrElements = Array.from(el.value.querySelectorAll('.tldr.desktop, .tldr.mobile'))
	return tldrElements.find((tldrElement) => window.getComputedStyle(tldrElement).display !== 'none') || null
}

const getAnimationTargets = () => {
	if (!el.value) {
		return {
			backgroundElement: null,
			sideSupportElements: [],
			categoryTitleElement: null,
			titleElement: null,
			specsElement: null,
			subtitleElement: null,
			personsElement: null,
			imageElement: null,
		}
	}

	return {
		backgroundElement: el.value.querySelector('.inner-bg'),
		sideSupportElements: Array.from(el.value.querySelectorAll('.back-wrapper, .side-content .tldr.desktop, .side-content .call-to-action.desktop')),
		categoryTitleElement: el.value.querySelector('.main-content .category-title'),
		titleElement: el.value.querySelector('.main-content .main-title'),
		specsElement: el.value.querySelector('.main-content .specs'),
		subtitleElement: el.value.querySelector('.main-content .subtitle'),
		personsElement: el.value.querySelector('.main-content .persons'),
		imageElement: el.value.querySelector('.main-content .image-wrapper'),
	}
}

const setup = () => {
	if (!import.meta.client || !el.value) return

	const { backgroundElement, sideSupportElements, categoryTitleElement, titleElement, specsElement, subtitleElement, personsElement, imageElement } = getAnimationTargets()
	tldrForEntry = getVisibleTldrElement()

	backgroundElement && gsap.set(backgroundElement, { scale: 0.82, transformOrigin: '50% 50%', autoAlpha: 0 })
	sideSupportElements.length && gsap.set(sideSupportElements, { y: 24, autoAlpha: 0 })
	categoryTitleElement && gsap.set(categoryTitleElement, { y: 24, autoAlpha: 0 })
	titleElement && gsap.set(titleElement, { autoAlpha: 0 })
	specsElement && gsap.set(specsElement, { y: 24, autoAlpha: 0 })
	tldrForEntry && gsap.set(tldrForEntry, { y: 24, autoAlpha: 0 })
	subtitleElement && gsap.set(subtitleElement, { y: 24, autoAlpha: 0 })
	personsElement && gsap.set(personsElement, { y: 24, autoAlpha: 0 })
	imageElement && gsap.set(imageElement, { y: 24, autoAlpha: 0 })
}

const init = async () => {
	if (!import.meta.client || !el.value || hasAnimated) return
	hasAnimated = true
	emitter.emit('page-header-animation-start', { routeKey: headerRouteKey })

	await nextTick()

	const { backgroundElement, sideSupportElements, categoryTitleElement, titleElement, specsElement, subtitleElement, personsElement, imageElement } = getAnimationTargets()
	const visibleTldrElement = tldrForEntry

	gsap.context(() => {
		splitTitle?.revert()
		splitTitle = titleElement ? new SplitText(titleElement, { type: 'lines', linesClass: 'split-line' }) : null
		const titleLines = splitTitle?.lines || []

		titleLines.length && gsap.set(titleLines, { yPercent: 110, autoAlpha: 0 })
		titleElement && gsap.set(titleElement, { autoAlpha: 1 })

		entryTimeline = gsap.timeline()

		backgroundElement &&
			entryTimeline.to(
				backgroundElement,
				{
					scale: 1,
					autoAlpha: 1,
					duration: 1.1,
					ease: 'power3.out',
				},
				0
			)

		sideSupportElements.length &&
			entryTimeline.to(
				sideSupportElements,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.6,
					stagger: 0.08,
					ease: 'power3.out',
				},
				0.18
			)

		categoryTitleElement &&
			entryTimeline.to(
				categoryTitleElement,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.6,
					ease: 'power3.out',
				},
				0.24
			)

		titleLines.length &&
			entryTimeline.to(
				titleLines,
				{
					yPercent: 0,
					autoAlpha: 1,
					duration: 0.75,
					stagger: 0.08,
					ease: 'power3.out',
				},
				0.3
			)

		specsElement &&
			entryTimeline.to(
				specsElement,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.6,
					ease: 'power3.out',
				},
				0.42
			)

		visibleTldrElement &&
			entryTimeline.to(
				visibleTldrElement,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.6,
					ease: 'power3.out',
				},
				0.5
			)

		subtitleElement &&
			entryTimeline.to(
				subtitleElement,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.6,
					ease: 'power3.out',
				},
				0.58
			)

		personsElement &&
			entryTimeline.to(
				personsElement,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.6,
					ease: 'power3.out',
				},
				0.66
			)

		imageElement &&
			entryTimeline.to(
				imageElement,
				{
					y: 0,
					autoAlpha: 1,
					duration: 0.65,
					ease: 'power3.out',
				},
				0.74
			)
	}, el.value)
}

const kill = () => {
	entryTimeline?.kill()
	entryTimeline = null

	splitTitle = null
	tldrForEntry = null
}

onBeforeUnmount(() => kill())
onMounted(() => {
	const loader = context?.$page?.loader
	setup()

	if (!loader) {
		init()
		return
	}

	loader.ready.then(() => init())
})
</script>

<style lang="scss" scoped>
[data-component='header'] {
	position: relative;

	display: flex;

	width: 100%;
	// min-height: 100vh;

	@include max-width(821px) {
		min-height: auto;
	}

	.inner-bg {
		position: absolute;
		inset: rs(16, 12);
		border-radius: rs(34, 24);
		background-color: var(--color-river-blue-50);
		overflow: hidden;
		transform: scale(0.82);
		transform-origin: 50% 50%;
		opacity: 0;
		visibility: hidden;
		will-change: transform, opacity;

		z-index: 1;

		.fade {
			position: absolute;
			bottom: 0;
			width: 100%;
			height: rs(450);
			background: linear-gradient(0deg, #fff 15%, rgba(250, 253, 253, 0) 100%);
			z-index: 1;

			@include max-width($xs) {
				height: rs(550, 550);
				bottom: 100px;
				background: linear-gradient(0deg, #fff 35%, rgba(250, 253, 253, 0) 100%);
			}
		}
	}

	.g-row {
		position: relative;
		z-index: 2;

		@include max-width($xs) {
			flex-direction: column;
			flex-wrap: nowrap;
		}
	}

	.tldr {
		&.mobile {
			margin-left: column-width(2, true);
			margin-top: 50px;
			opacity: 0;
			visibility: hidden;
			transform: translateY(#{rs(24, 24)});
			will-change: transform, opacity;

			border-top: 1px solid var(--color-river-blue-100);
			border-bottom: 1px solid var(--color-river-blue-100);

			padding: 10px 0;

			@include max-width($xs) {
				margin-top: 50px;
				margin-left: column-width(4, true);
			}
		}
	}

	.header-row {
		padding-top: rs(124, 124);
		align-items: flex-start;
		align-content: flex-start;
		flex-wrap: nowrap;

		@include max-width($xs) {
			> .g-col {
				flex: 0 0 auto;
			}
		}

		.side-content {
			height: auto;
			align-self: flex-start;
			display: flex;
			flex-direction: column;

			padding-top: rs(56, 24);

			.back-wrapper {
				margin-bottom: rs(230, 30);
				display: flex;
				align-items: center;
				gap: rs(12, 12);
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				.label {
					color: var(--color-river-blue-500);
				}
			}

			.tldr {
				flex-direction: column;
				padding-top: rs(16, 16);
				border-top: 1px solid var(--color-river-blue-100);
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				.title {
					margin-bottom: rs(6, 6);
				}

				&.mobile {
					@include max-width($xs) {
						margin-left: column-width(4, true);
					}
				}
			}

			.call-to-action {
				flex-direction: column;
				align-items: flex-start;
				opacity: 0;
				visibility: hidden;
				will-change: transform, opacity;

				position: absolute;
				top: 100vh;
				width: column-width(3);
				transform: translateY(-150%);

				.title {
					margin-bottom: rs(15, 15);
				}
			}
		}

		.main-content {
			.category-title {
				color: var(--color-river-blue-500);
				text-indent: column-width(2, true);
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				@include max-width($xs) {
					text-indent: column-width(4, true);
				}

				:deep(div) {
					@include inherit-font;
				}
			}
			.main-title {
				color: var(--color-river-blue-900);
				opacity: 0;
				visibility: hidden;
				will-change: opacity;

				:deep(div) {
					@include inherit-font;
				}
			}

			.main-title :deep(.split-line) {
				display: block;
				line-height: inherit;
			}

			.main-title :deep(.split-line > *) {
				display: block;
				line-height: inherit;
			}
			.specs {
				margin-top: rs(12, 12);
				margin-left: column-width(2, true);
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				display: flex;
				gap: rs(12, 12);

				@include max-width($xs) {
					margin-left: column-width(4, true);
				}

				span {
					@include inherit-font;
					color: var(--color-river-blue-900);
				}
			}
			.subtitle {
				margin-top: rs(102, 52);
				margin-bottom: rs(32, 32);
				margin-left: column-width(2, true);
				color: var(--color-river-blue-900);
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				@include max-width($xs) {
					margin-left: column-width(4, true);
				}
			}

			.persons {
				margin-top: rs(32, 32);
				margin-bottom: rs(32, 32);
				margin-left: column-width(2, true);
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				@include max-width($xs) {
					margin-left: column-width(4, true);
				}

				.title {
					color: var(--color-neutral-tints-500);
				}

				.persons-wrapper {
					display: flex;
					flex-direction: row;
					align-items: center;
					gap: rs(12, 12);
					margin-top: rs(20, 20);

					@include max-width($xs) {
						flex-direction: column;
						align-items: flex-start;
					}
				}

				.author-meta {
					display: flex;
					flex-direction: row;
					align-items: center;
					gap: rs(8, 8);

					.author-image-wrapper {
						width: rs(52, 36);
						height: rs(52, 36);

						overflow: hidden;
						border-radius: 100%;
					}
					.author-name {
						color: var(--color-neutral-tints-700);
					}
					.author-role {
						color: var(--color-neutral-tints-500);
					}
				}
			}

			.image-wrapper {
				position: relative;
				// border-radius: rs(12, 12);
				transform: translateZ(0);
				isolation: isolate;
				overflow: visible;
				opacity: 0;
				visibility: hidden;
				transform: translateY(#{rs(24, 24)});
				will-change: transform, opacity;

				margin-left: column-width(2, true);

				@include max-width($xs) {
					margin-left: calc(-1 * #{column-width(1, true)});
					width: calc(100% + #{column-width(2, true)});
				}

				.media-mask {
					position: relative;
					z-index: 1;
					width: 100%;
					height: 100%;
					display: block;
					-webkit-clip-path: var(--article-header-media-clip-path);
					clip-path: var(--article-header-media-clip-path);
				}

				.media-mask :deep([data-component='media-asset']),
				.media-mask :deep([data-component='image-asset']),
				.media-mask :deep([data-component='video-asset']) {
					display: block;
					width: 100%;
					height: 100%;
				}

				.media-mask :deep(img),
				.media-mask :deep(video),
				img {
					position: relative;
					z-index: 1;
					display: block;
					width: 100%;
					height: auto;

					@include max-width($xs) {
						margin-left: 0;
						width: calc(100% + #{column-width(2, true)});
					}
				}

				.svg-mask {
					position: absolute;
					width: 0;
					height: 0;
					overflow: hidden;
					pointer-events: none;
				}
			}
		}
	}

	.mobile {
		display: none;
		visibility: hidden;
		pointer-events: none;
	}
	.desktop {
		display: flex;
	}

	@include max-width($sm) {
		.mobile {
			display: block;
			visibility: visible;
			pointer-events: all;
		}
		.desktop {
			display: none;
			visibility: hidden;
			pointer-events: none;
		}
	}
}
</style>
